import _extends from "@babel/runtime/helpers/extends";
import { i18n } from "@lingui/core";
import { formNames, MktSelectors } from '@wkda/carlead-creation-form';
import { useAppConfiguration } from '@wkda/funnel-components';
import { formatThousand, leaveOnlyNumber } from '@wkda/funnel-utils';
import { AlertBox } from '@wkda/styleguide';
import React, { useCallback, useRef } from 'react';
import { useField } from 'react-final-form';
import { useCarleadFieldChangedTracking } from '../../hooks/useCarleadFieldChangedTracking';
import { BaseField } from '../BaseField';
import { selectFieldRenderer, textInputRenderer } from '../fieldRenderers';
import { useStateMachineForFields } from '../hooks';
import { mileageData } from './mileageData';
export function CarleadMileage(_ref) {
  let {
    label = i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.label.mileage'
    }),
    placeholder = i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.placeholder.mileage'
    }),
    options = mileageData,
    className,
    onChange,
    ...props
  } = _ref;
  const name = formNames.mileage;
  const {
    isFieldEnabled,
    stateFieldClassNames,
    sendUserSelect
  } = useStateMachineForFields({
    name
  });
  return /*#__PURE__*/React.createElement(BaseField, _extends({
    label: label,
    name: name,
    MktSelector: MktSelectors.mileage,
    topContainerClassName: stateFieldClassNames,
    disabled: !isFieldEnabled,
    renderFieldComponent: function fieldRenderer(_ref2) {
      let {
        input,
        meta
      } = _ref2;
      return selectFieldRenderer({
        input,
        meta,
        placeholder,
        className,
        options: options,
        disabled: !isFieldEnabled,

        onChange(evt) {
          input.onChange(evt);
          onChange && onChange(evt);
          sendUserSelect(evt.target.value);
        }

      });
    }
  }, props));
}
export function CarleadMileageText(_ref3) {
  let {
    label = i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.label.mileage',
      message: 'Kilometer'
    }),
    placeholder = i18n._(
    /*i18n*/
    {
      id: 'carleadCreation.form.placeholder.mileage',
      message: 'Kilometer auswählen'
    }),
    inputSuffix = i18n._(
    /*i18n*/
    {
      id: 'selfInspection.distance.km'
    }),
    className,
    onChange,
    pattern = '[0-9]*',
    matchPattern = /^[0-9]*$/,
    parse = v => v,
    ...props
  } = _ref3;
  const name = formNames.mileage;
  const {
    isFieldEnabled,
    stateFieldClassNames,
    sendSelect,
    sendFocus,
    fillInput
  } = useStateMachineForFields({
    name
  });
  const trackLeadFieldChanged = useCarleadFieldChangedTracking();
  const internalPreviousValue = useRef('');

  function onFocus(e) {
    sendFocus();
    internalPreviousValue.current = parse(e.target.value);
  }

  function onBlur(e) {
    if (internalPreviousValue.current !== parse(e.target.value)) {
      trackLeadFieldChanged(name, 'user');
    }

    sendSelect(parse(e.target.value));
  }

  return /*#__PURE__*/React.createElement(BaseField, _extends({
    label: label,
    name: name,
    MktSelector: MktSelectors.mileage,
    topContainerClassName: stateFieldClassNames,
    disabled: !isFieldEnabled,
    renderFieldComponent: function fieldRenderer(_ref4) {
      let {
        input,
        meta
      } = _ref4;
      return textInputRenderer({
        id: name,
        input,
        meta,
        placeholder,
        className,
        disabled: !isFieldEnabled,
        inputMode: 'numeric',
        pattern,
        matchPattern,
        inputSuffix,
        onFocus,
        onBlur,

        onChange(value) {
          input.onChange(value);
          fillInput(parse(value));
        }

      });
    },
    parse: parse
  }, props));
}
export function CarleadMileageFormatted(_ref5) {
  let {
    renderSuspiciousMileageMessage = function () {
      return /*#__PURE__*/React.createElement(SuspiciousMileageInfo, null);
    },
    ...props
  } = _ref5;
  const {
    locale
  } = useAppConfiguration();
  const formatMileage = useCallback(value => formatThousand(locale, value), [locale]);
  const parseMileage = useCallback(value => leaveOnlyNumber(value != null ? String(value) : ''), []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CarleadMileageText, _extends({
    format: formatMileage,
    parse: parseMileage,
    pattern: null,
    matchPattern: null,
    renderInfoText: function renderInfoText(text) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, Boolean(text) ? /*#__PURE__*/React.createElement("span", {
        className: "font-size-xs d-block mt-1 text-muted"
      }, text) : null, renderSuspiciousMileageMessage());
    }
  }, props)));
}
const UPPER_THRESHOLD_YEAR = 35000;
const LOWER_THRESHOLD_YEAR = 2500;

function checkIfMileageSuspicious(year, mileage) {
  if (!mileage) {
    return false;
  }

  const howOldIsCar = new Date().getFullYear() - year;
  const upperThreshold = howOldIsCar * UPPER_THRESHOLD_YEAR;
  const lowerThreshold = howOldIsCar * LOWER_THRESHOLD_YEAR;
  return mileage < lowerThreshold || mileage > upperThreshold;
}

export function SuspiciousMileageInfo(_ref6) {
  let {
    suspiciousMileageMessage = i18n._(
    /*i18n*/
    {
      id: 'selfInspection.error.suspiciousMileage'
    })
  } = _ref6;
  const {
    input: {
      value: builtDate
    }
  } = useField(formNames.builtDate, {
    subscription: {
      value: true
    }
  });
  const {
    input: {
      value: mileageValue
    }
  } = useField(formNames.mileage, {
    subscription: {
      value: true
    }
  });
  const isSuspiciousInput = checkIfMileageSuspicious(builtDate, mileageValue);

  if (!isSuspiciousInput) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: "mt-1"
  }, /*#__PURE__*/React.createElement(AlertBox, {
    variant: "info",
    design: "complete"
  }, suspiciousMileageMessage));
}